import { useState } from "react";
import {
  Circle,
  CircleMarker,
  MapContainer,
  Rectangle,
  TileLayer,
  Tooltip,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  ACCEPTED_BOUNDS,
  CAMPUS,
  GPS_BAD_ACCURACY_M,
  GPS_OK_ACCURACY_M,
  isWithinBounds,
} from "../geo/bounds";
import type { CaptureBlock, GpsFix } from "../types";

export type MapPin = { latitude: number; longitude: number } | null;

type Props = {
  blocks: CaptureBlock[];
  gps: GpsFix | null;
  pin: MapPin;
  onPick?: (latitude: number, longitude: number) => void;
};

function ClickCatcher({ onPick }: { onPick: (lat: number, lon: number) => void }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function ZoneRectangle() {
  const { minLat, minLon, maxLat, maxLon } = ACCEPTED_BOUNDS;
  return (
    <Rectangle
      bounds={[
        [minLat, minLon],
        [maxLat, maxLon],
      ]}
      pathOptions={{ color: "#2ea043", weight: 1.5, dashArray: "6 4", fillOpacity: 0.06 }}
    />
  );
}

export default function MapPane({ blocks, gps, pin, onPick }: Props) {
  const [position] = useState<[number, number]>([CAMPUS.latitude, CAMPUS.longitude]);
  const pinInBounds = pin ? isWithinBounds(pin.latitude, pin.longitude) : false;

  return (
    <div className="map-wrap">
      <MapContainer center={position} zoom={16} className="map" zoomControl attributionControl>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ZoneRectangle />
        {onPick && <ClickCatcher onPick={onPick} />}

        {gps &&
          (() => {
            const radius = gps.accuracy ?? GPS_OK_ACCURACY_M;
            const bad = radius > GPS_BAD_ACCURACY_M;
            return (
              <>
                <Circle
                  center={[gps.latitude, gps.longitude]}
                  radius={radius}
                  pathOptions={{
                    color: bad ? "#f85149" : "#3b82f6",
                    weight: 1,
                    fillColor: bad ? "#f85149" : "#3b82f6",
                    fillOpacity: 0.12,
                  }}
                />
                <CircleMarker
                  center={[gps.latitude, gps.longitude]}
                  radius={5}
                  pathOptions={{
                    color: "#fff",
                    weight: 1,
                    fillColor: bad ? "#f85149" : "#3b82f6",
                    fillOpacity: 1,
                  }}
                >
                  <Tooltip direction="top" offset={[0, -8]}>
                    GPS {gps.accuracy !== undefined ? `${Math.round(gps.accuracy)} m` : "…"}
                  </Tooltip>
                </CircleMarker>
              </>
            );
          })()}

        {pin && pinInBounds && (
          <CircleMarker
            center={[pin.latitude, pin.longitude]}
            radius={7}
            pathOptions={{ color: "#fff", weight: 1.5, fillColor: "#d29922", fillOpacity: 1 }}
          >
            <Tooltip direction="top" offset={[0, -8]}>
              Map pin — capture with “Use pin”
            </Tooltip>
          </CircleMarker>
        )}

        {blocks.map((b) => (
          <CircleMarker
            key={b.name}
            center={[b.latitude, b.longitude]}
            radius={6}
            pathOptions={{ color: "#fff", weight: 1, fillColor: "#2ea043", fillOpacity: 1 }}
          >
            <Tooltip direction="top" offset={[0, -8]} permanent opacity={0.9}>
              {b.label ?? b.name}
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>

      <div className="map-legend">
        <span>
          <i className="dot dot-gps" /> GPS fix
        </span>
        <span>
          <i className="dot dot-pin" /> map pin
        </span>
        <span>
          <i className="dot dot-captured" /> captured
        </span>
        <span>
          <i className="dot dot-zone" /> capture zone
        </span>
      </div>
    </div>
  );
}